/**
 * Admin Home Groups Page: list of home groups.
 * Native mobile app style.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import {
  IconPlus,
  IconAlertTriangle,
  IconHomeHeart,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { getErrorMessage } from "../../../lib/graphql.ts";
import { useAdminHomeGroups } from "./hooks";
import {
  formatAccountName,
  DAY_OF_WEEK_LABELS,
  type AdminHomeGroupsFilter,
} from "./types.ts";

export function AdminHomeGroupsPage() {
  const navigate = useNavigate();
  const tg = getTelegram();

  const [filter, setFilter] = useState<AdminHomeGroupsFilter>({});
  const { groups, isLoading, error, refetch } = useAdminHomeGroups(filter);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  if (isLoading && groups.length === 0) return <PageLoader message="Загрузка домашних групп..." />;

  if (error) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">
            Ошибка загрузки
          </h2>
          <p className="text-sm text-slate-500 mb-4">{getErrorMessage(error, "Не удалось загрузить данные")}</p>
          <button onClick={refetch} className="btn-primary">
            Попробовать снова
          </button>
        </div>
      </Layout>
    );
  }

  const activeCount = groups.filter((g) => g.isActive).length;

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-600">
          <IconHomeHeart className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">
            Домашние группы
          </h1>
          <p className="text-[13px] text-slate-500">
            {activeCount} активных из {groups.length}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic("light");
            navigate("/admin/home-groups/new");
          }}
          className="w-10 h-10 rounded-xl bg-[var(--color-accent)] text-white flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Создать группу"
        >
          <IconPlus className="w-5 h-5" />
        </button>
      </div>

      {/* Simple filter */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => {
            haptic("light");
            setFilter({});
          }}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${
            Object.keys(filter).length === 0
              ? "bg-slate-700 text-white"
              : "bg-slate-100 text-slate-600"
          }`}
        >
          Все
        </button>
        <button
          type="button"
          onClick={() => {
            haptic("light");
            setFilter({ isActive: true });
          }}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${
            filter.isActive === true ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"
          }`}
        >
          Активные
        </button>
        <button
          type="button"
          onClick={() => {
            haptic("light");
            setFilter({ isOpen: true });
          }}
          className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${
            filter.isOpen === true ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
          }`}
        >
          Открытые
        </button>
      </div>

      {/* Empty state */}
      {groups.length === 0 && (
        <div className="card p-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-slate-100 flex items-center justify-center">
            <IconHomeHeart className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="text-[15px] font-semibold text-slate-900 mb-2">
            Нет домашних групп
          </h3>
          <p className="text-[13px] text-slate-500 mb-4">
            {Object.keys(filter).length
              ? "Попробуйте изменить фильтр"
              : "Создайте первую домашнюю группу"}
          </p>
          {Object.keys(filter).length ? (
            <button
              type="button"
              onClick={() => setFilter({})}
              className="text-[14px] text-[var(--color-accent)] font-medium"
            >
              Сбросить фильтр
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                haptic("light");
                navigate("/admin/home-groups/new");
              }}
              className="btn-primary"
            >
              Создать группу
            </button>
          )}
        </div>
      )}

      {/* List */}
      {groups.length > 0 && (
        <div className="card overflow-hidden">
          {groups.map((group, idx) => {
            const scheduleSummary = group.schedules.length
              ? group.schedules
                  .slice(0, 2)
                  .map(
                    (s) =>
                      `${DAY_OF_WEEK_LABELS[s.dayOfWeek]} ${s.startTime}`,
                  )
                  .join(", ")
              : null;
            return (
              <button
                key={group.id}
                type="button"
                onClick={() => {
                  haptic("light");
                  navigate(`/admin/home-groups/${group.id}`);
                }}
                className={`w-full flex items-center gap-3 min-h-[72px] px-4 py-3 active:bg-slate-50 transition-colors text-left ${
                  idx > 0 ? "border-t border-slate-100" : ""
                } ${!group.isActive ? "opacity-60" : ""}`}
              >
                <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                  <IconHomeHeart className="w-5 h-5 text-rose-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-slate-900 text-[15px] truncate">
                      {group.name}
                    </span>
                    {!group.isActive && (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold text-slate-400 bg-slate-100 rounded">
                        СКРЫТА
                      </span>
                    )}
                    {group.isOpen && (
                      <span className="px-1.5 py-0.5 text-[9px] font-medium text-emerald-600 bg-emerald-50 rounded">
                        Открыта
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-slate-500">
                    {group.leader ? formatAccountName(group.leader) : "Нет лидера"}
                    {group.location && ` · ${group.location}`}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-slate-400">
                      {group.memberCount} участников
                    </span>
                    {scheduleSummary && (
                      <span className="text-[11px] text-slate-400 truncate">
                        {scheduleSummary}
                      </span>
                    )}
                  </div>
                </div>
                <svg
                  className="w-5 h-5 text-slate-300 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            );
          })}
        </div>
      )}

      <div className="h-6" />
    </Layout>
  );
}
